import { useCallback, useEffect, useRef, useState } from 'react';

import type { Event } from '@rive-app/react-canvas';
import { Alignment, Fit, Layout } from '@rive-app/react-canvas';

import { useRiveBingoPrizeUpdater } from '../../../hooks/rive/useRiveBingoPrizeUpdater';
import { useRiveMachine } from '../../../hooks/rive/useRiveMachine';
import { logToNewRelic } from '../../../services/debug';
import { useFlag } from '../../../services/launchDarkly';
import type { RiveFTUEConfig } from '../../../services/types';
import { useStores } from '../../../stores/useStores';

import styles from './bingoFTUE.module.scss';
import type { BingoFtueProgress, BingoRiveEventProperties } from './types';

export type BingoFtueEntryPoint = 'cc' | 'bingo';

export type BingoFtueInteractionType = 'click' | 'impression';

export interface BingoFTUERiveProps {
  onBingoFtueEvent: (
    properties: BingoRiveEventProperties,
    fileUrl: string,
    interactionType?: BingoFtueInteractionType,
  ) => void;
  terminateBingoFtueFlow: (reason: string) => void;
  viewType?: 'desktop' | 'mobile';
  savedStep: BingoFtueProgress | null;
  onRiveReady?: () => void;
  entryPoint?: BingoFtueEntryPoint;
}

const getMaxStepFromInputs = (inputs: { name: string }[] | null): number => {
  if (!inputs) return 0;

  let maxStep = 0;
  for (const input of inputs) {
    const match = input.name.match(/^(\d+)_/);
    if (match) {
      const stepNum = parseInt(match[1], 10);
      if (stepNum > maxStep) {
        maxStep = stepNum;
      }
    }
  }
  return maxStep;
};

const isLastStep = (stageNum: number | undefined, maxStep: number, lastStepTriggered: boolean): boolean => {
  return !lastStepTriggered && stageNum !== undefined && maxStep > 0 && stageNum === maxStep;
};

export const BingoFTUERive = ({
  onBingoFtueEvent,
  terminateBingoFtueFlow,
  viewType,
  savedStep,
  onRiveReady,
  entryPoint = 'cc',
}: BingoFTUERiveProps) => {
  const { userStore } = useStores();

  const riveConfig = useFlag<RiveFTUEConfig | null>('bingo_ftue_config', null);
  const fileConfig = viewType === 'desktop' ? riveConfig?.desktop : riveConfig?.mobile_m;

  const fileConfigMatch =
    savedStep?.firebaseUserId === userStore.profile?.firebaseUid && savedStep?.riveFileName === fileConfig?.url;

  const stateMachineName = fileConfig?.stateMachineName || 'State Machine 1';

  const maxStepRef = useRef<number>(0);
  const lastStepTriggeredRef = useRef<boolean>(false);
  const riveRef = useRef<boolean>(false);
  const skipTutorialFiredRef = useRef<boolean>(false);
  const bingoBGStartFiredRef = useRef<boolean>(false);
  const advanceSourceRef = useRef<BingoFtueInteractionType>('click');
  const [currentStep, setCurrentStep] = useState<number>(0);

  const handleLoadError = useCallback(
    (error: unknown) => {
      terminateBingoFtueFlow(String(error) || 'rive_load_error');
    },
    [terminateBingoFtueFlow],
  );

  const handleEventWithProperties = useCallback(
    (properties: Record<string, unknown>) => {
      if (!fileConfig) return;

      if (properties.isSkip === true) {
        onBingoFtueEvent({ isSkip: true }, fileConfig.url);
        return;
      }

      const stageNum = typeof properties.stage_num === 'number' ? properties.stage_num : undefined;

      if (stageNum !== undefined) {
        setCurrentStep(stageNum);
      }

      if (isLastStep(stageNum, maxStepRef.current, lastStepTriggeredRef.current)) {
        lastStepTriggeredRef.current = true;
        onBingoFtueEvent({ isFinish: true }, fileConfig.url);
        return;
      }

      const eventProperties: BingoRiveEventProperties = {
        event_name: String(properties.event_name || ''),
        step_name: String(properties.step_name || ''),
        stageNum,
        isLastStep: false,
      };

      const interactionType = advanceSourceRef.current;
      advanceSourceRef.current = 'click';
      onBingoFtueEvent(eventProperties, fileConfig.url, interactionType);
    },
    [onBingoFtueEvent, fileConfig],
  );

  const handleRiveEvent = useCallback(
    (event: Event) => {
      console.log('🚀 ~ :123 ~ BingoFTUERive ~ event:', event);
      if (typeof event.data !== 'object' || !fileConfig?.url) {
        return;
      }

      if ('properties' in event.data && event.data.properties) {
        handleEventWithProperties(event.data.properties);
        return;
      }
    },
    [fileConfig?.url, handleEventWithProperties],
  );

  const { rive, RiveComponent, useStateMachineInput, fireRiveViewModelTrigger, setRiveViewModelEnum } = useRiveMachine({
    src: fileConfig?.url,
    stateMachineName,
    autoplay: false,
    autoBind: true,
    artboard: 'Main',
    layout: new Layout({
      fit: Fit.Contain,
      alignment: viewType === 'mobile' ? Alignment.TopCenter : Alignment.Center,
    }),
    onLoad: onRiveReady,
    onLoadError: handleLoadError,
    onRiveEvent: handleRiveEvent,
  });

  useRiveBingoPrizeUpdater({
    rive,
  });

  useEffect(() => {
    if (!rive) return;

    if (entryPoint && !bingoBGStartFiredRef.current) {
      setRiveViewModelEnum('bingoBG', entryPoint);
      setRiveViewModelEnum('lobbyBG', entryPoint); // TODO: remove after rive file is updated
      bingoBGStartFiredRef.current = true;
    }

    rive.play();

    try {
      const inputs = rive.stateMachineInputs(stateMachineName);
      if (inputs) {
        maxStepRef.current = getMaxStepFromInputs(inputs);
      }
    } catch (error) {
      console.error('Failed to get Rive inputs:', error);
      logToNewRelic('BingoFTUE', { message: 'State machine might not be ready yet' }, 'error');
    }
  }, [rive, stateMachineName, entryPoint, setRiveViewModelEnum]);

  const savedStepTriggerName =
    savedStep?.stepName && savedStep?.stageNum
      ? `${savedStep.stageNum}_${savedStep.stepName.toLowerCase().replace(/^\d+_/, '')}`
      : null;
  const savedStepTrigger = useStateMachineInput(rive, stateMachineName, savedStepTriggerName || '', true);

  useEffect(() => {
    const shouldRestoreStep = rive && savedStepTrigger && savedStepTriggerName && fileConfigMatch && !riveRef.current;

    if (shouldRestoreStep) {
      savedStepTrigger.fire();
      riveRef.current = true;
    }
  }, [rive, savedStepTrigger, savedStepTriggerName, fileConfigMatch]);

  useEffect(() => {
    if (!rive || !fileConfig || skipTutorialFiredRef.current) return;

    const shouldShowSkipButton = fileConfig.skip_tutorial !== undefined && currentStep >= fileConfig.skip_tutorial;
    if (!shouldShowSkipButton) return;

    const ok = fireRiveViewModelTrigger('skipTutorial');
    if (ok) skipTutorialFiredRef.current = true;
  }, [rive, fileConfig, currentStep, fireRiveViewModelTrigger]);

  useEffect(() => {
    if (!rive) return;

    const intervalId = setInterval(() => {
      advanceSourceRef.current = 'impression';
      fireRiveViewModelTrigger('next');
    }, 5000);

    return () => clearInterval(intervalId);
  }, [rive, fireRiveViewModelTrigger]);

  useEffect(() => {
    if (!riveConfig || !fileConfig) {
      terminateBingoFtueFlow('missing_rive_config');
    }
  }, [riveConfig, fileConfig, terminateBingoFtueFlow]);

  if (!riveConfig || !fileConfig) {
    return null;
  }

  return (
    <div className={styles.riveContainer}>
      <RiveComponent className={styles.animationContainer} />
    </div>
  );
};
